// ==========================
// FILE: src/components/MemoryLane.js
// ==========================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "../firebase";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import Modal from "./ui/Modal";
import "../styles/layout.css";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";

// NEW: manager-mode awareness + new video player
import { useAccess } from "../access/AccessContext";
import VideoLightbox from "./ui/VideoLightbox";

// ✅ Standard print/download utilities
import { printEntry, downloadEntry } from "../utils/printDownload";

// --- helpers kept from your file ---
function toDate(val) {
  if (!val) return null;
  try {
    if (typeof val?.toDate === "function") return val.toDate();
    if (typeof val === "number") return new Date(val);
    const d = new Date(String(val));
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}
function fmtDateInput(anyDateLike) {
  const d = toDate(anyDateLike);
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function derivePathFromUrl(u) {
  try { const enc = u?.split("/o/")[1]?.split("?")[0] || ""; return enc ? decodeURIComponent(enc) : null; } catch { return null; }
}

// ✅ Helpers for export/forward
function saveTextFile(filename, text) {
  const blob = new Blob([text || ""], { type: "text/plain;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename || "memory.txt";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
}
function buildSummaryText(mem) {
  const lines = [];
  lines.push("Memory");
  lines.push(`Generated: ${new Date().toLocaleString()}`);
  lines.push("");
  lines.push(`Title: ${mem?.title || "—"}`);
  const d = toDate(mem?.date) || toDate(mem?.created);
  lines.push(`Date: ${d ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : (mem?.date || "—")}`);
  lines.push(`Description: ${mem?.description || "—"}`);
  const tags = Array.isArray(mem?.tags) ? mem.tags.join(", ") : (mem?.tags || "");
  lines.push(`Tags: ${tags || "—"}`);
  const imgs = Array.isArray(mem?.images) ? mem.images : [];
  if (imgs.length) {
    lines.push("");
    lines.push("Images:");
    imgs.forEach((u, i) => lines.push(`${i + 1}. ${u}`));
  }
  if (mem?.videoUrl) { lines.push(""); lines.push(`Video: ${mem.videoUrl}`); }
  if (mem?.audioUrl) { lines.push(""); lines.push(`Audio: ${mem.audioUrl}`); }
  return lines.join("\n");
}

function defaultForm(uid = "") {
  return {
    uid,
    title: "",
    date: "", // yyyy-mm-dd
    description: "",
    tags: "",
    images: [], // saved URLs
    videoUrl: "", // saved URL (single)
    audioUrl: "", // saved URL (single)
    mediaFiles: [], // [{file, type, previewUrl}]
  };
}

export default function MemoryLane({ user }) {
  const { readOnly } = useAccess() || { readOnly: false };

  const [memories, setMemories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [form, setForm] = useState(defaultForm(user?.uid));
  const [editingId, setEditingId] = useState(null);
  const [editingMem, setEditingMem] = useState(null);
  const [uploading, setUploading] = useState(false);

  // View-only summary modal state
  const [viewMem, setViewMem] = useState(null);

  // DP UI
  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // year filter
  const [year, setYear] = useState("all");
  const filterBarRef = useRef(null);

  const fileInputMedia = useRef(null);

  // Lightbox state
  const [lightbox, setLightbox] = useState(null); // { items:[{type,url}], index }

  // Fetch memories
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "memories"),
      where("uid", "==", user.uid),
      orderBy("created", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setMemories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user?.uid]);

  // Open / close form
  function openForm(memory = null) {
    if (!memory && free && memories.length >= 1) { setShowUpgrade(true); return; }
    if (readOnly) return; // ✅ managers can't open editor
    if (memory) {
      setForm({
        uid: user.uid,
        title: memory.title || "",
        date: fmtDateInput(memory.date) || "",
        description: memory.description || "",
        tags: Array.isArray(memory.tags) ? memory.tags.join(", ") : (memory.tags || ""),
        images: Array.isArray(memory.images) ? memory.images : [],
        videoUrl: memory.videoUrl || "",
        audioUrl: memory.audioUrl || "",
        mediaFiles: [],
      });
      setEditingId(memory.id);
      setEditingMem(memory);
    } else {
      setForm(defaultForm(user.uid));
      setEditingId(null);
      setEditingMem(null);
    }
    setShowForm(true);
  }
  function closeForm() { setShowForm(false); }

  // Unified file handler (image/video/audio)
  function handleMediaFiles(e) {
    const files = Array.from(e.target.files || []);
    const mapped = files.map((file) => ({ file, type: file.type || "", previewUrl: URL.createObjectURL(file) }));
    for (const m of form.mediaFiles) { try { URL.revokeObjectURL(m.previewUrl); } catch {} }
    setForm((f) => ({ ...f, mediaFiles: mapped }));
  }
  function removeNewMedia(idx) {
    setForm((f) => {
      const list = [...(f.mediaFiles || [])];
      const it = list[idx];
      if (it?.previewUrl) { try { URL.revokeObjectURL(it.previewUrl); } catch {} }
      list.splice(idx, 1);
      return { ...f, mediaFiles: list };
    });
  }

  // Upload helpers (append images, replace single video/audio)
  async function uploadMedia(mediaFiles) {
    const up = {
      images: [],
      imagesPaths: [],
      videoUrl: "",
      videoPath: "",
      audioUrl: "",
      audioPath: "",
    };

    for (const item of mediaFiles) {
      const f = item.file;
      if (!f) continue;
      const path = `memories/${user.uid}/${Date.now()}_${f.name}`;
      const refAny = storageRef(storage, path);
      const task = uploadBytesResumable(refAny, f);
      await new Promise((resolve, reject) => {
        task.on("state_changed", () => {}, reject, async () => {
          const url = await getDownloadURL(refAny);
          if (/^image\//i.test(f.type)) {
            up.images.push(url);
            up.imagesPaths.push(path);
          } else if (/^video\//i.test(f.type)) {
            if (!up.videoUrl) { up.videoUrl = url; up.videoPath = path; }
          } else if (/^audio\//i.test(f.type)) {
            if (!up.audioUrl) { up.audioUrl = url; up.audioPath = path; }
          }
          resolve();
        });
      });
    }
    return up;
  }

  async function handleSave(e) {
    e?.preventDefault?.();
    if (!user?.uid) return;
    setUploading(true);
    try {
      const uploaded = await uploadMedia(form.mediaFiles || []);

      const nextImages = [...(form.images || []), ...(uploaded.images || [])];
      const existingPaths = Array.isArray(editingMem?.imagesPaths) ? editingMem.imagesPaths.slice() : [];
      const nextImagesPaths = [...existingPaths, ...(uploaded.imagesPaths || [])];

      let videoUrl = form.videoUrl;
      let videoPath = editingMem?.videoPath || derivePathFromUrl(editingMem?.videoUrl) || "";
      let audioUrl = form.audioUrl;
      let audioPath = editingMem?.audioPath || derivePathFromUrl(editingMem?.audioUrl) || "";

      const replacingVideo = Boolean(uploaded.videoUrl);
      const replacingAudio = Boolean(uploaded.audioUrl);
      if (uploaded.videoUrl) { videoUrl = uploaded.videoUrl; videoPath = uploaded.videoPath; }
      if (uploaded.audioUrl) { audioUrl = uploaded.audioUrl; audioPath = uploaded.audioPath; }

      const tagsArray = String(form.tags || "").split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        uid: user.uid,
        title: form.title || "",
        date: form.date || "",
        description: form.description || "",
        tags: tagsArray,
        images: nextImages,
        imagesPaths: nextImagesPaths,
        videoUrl,
        videoPath,
        audioUrl,
        audioPath,
        updated: serverTimestamp(),
      };

      if (editingId) {
        if (replacingVideo && editingMem?.videoUrl) {
          const oldPath = editingMem?.videoPath || derivePathFromUrl(editingMem.videoUrl);
          if (oldPath) { try { await deleteObject(storageRef(storage, oldPath)); } catch {} }
        }
        if (replacingAudio && editingMem?.audioUrl) {
          const oldPath = editingMem?.audioPath || derivePathFromUrl(editingMem.audioUrl);
          if (oldPath) { try { await deleteObject(storageRef(storage, oldPath)); } catch {} }
        }
        await updateDoc(doc(db, "memories", editingId), payload);
      } else {
        await addDoc(collection(db, "memories"), { ...payload, created: serverTimestamp() });
      }

      for (const m of form.mediaFiles) { try { URL.revokeObjectURL(m.previewUrl); } catch {} }
      closeForm();
    } catch (err) {
      alert("Save failed: " + (err?.message || "Unknown error"));
    }
    setUploading(false);
  }

  async function handleDelete() {
    if (!editingMem?.id) return;
    if (!window.confirm("Delete this memory?")) return;
    try {
      const imgPaths = Array.isArray(editingMem?.imagesPaths) && editingMem.imagesPaths.length
        ? editingMem.imagesPaths
        : (Array.isArray(editingMem?.images) ? editingMem.images.map((u) => derivePathFromUrl(u)).filter(Boolean) : []);

      for (const p of imgPaths) { try { await deleteObject(storageRef(storage, p)); } catch {} }

      const vPath = editingMem?.videoPath || derivePathFromUrl(editingMem?.videoUrl);
      if (vPath) { try { await deleteObject(storageRef(storage, vPath)); } catch {} }
      const aPath = editingMem?.audioPath || derivePathFromUrl(editingMem?.audioUrl);
      if (aPath) { try { await deleteObject(storageRef(storage, aPath)); } catch {} }

      await deleteDoc(doc(db, "memories", editingMem.id));
      closeForm();
    } catch (e) {
      alert("Delete failed: " + (e?.message || "Unknown error"));
    }
  }

  // ===== Timeline data shaping =====
  const normalized = useMemo(() => memories.map((m) => {
    const d = toDate(m.date) || toDate(m.created);
    return { ...m, _date: d, _year: d ? d.getFullYear() : null };
  }), [memories]);

  const years = useMemo(() => {
    const set = new Set(normalized.filter((x) => x._year).map((x) => x._year));
    return ["all", ...Array.from(set).sort((a, b) => b - a)];
  }, [normalized]);

  const filtered = useMemo(() => {
    if (year === "all") return normalized;
    const y = Number(year);
    return normalized.filter((x) => x._year === y);
  }, [normalized, year]);

  // Header Actions (now manager-clickable)
  const ActionsDesktop = () => (
    <div className="dp-help-row">
      <button
        type="button"
        className="btn dp-help read"
        onClick={() => setShowDrawer(true)}
        data-allow-readonly
      >
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button
        type="button"
        className="btn dp-help see"
        onClick={() => setShowVideo(true)}
        data-allow-readonly
      >
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );
  const ActionsMobile = ActionsDesktop;

  // Lightbox helpers
  function openLightboxForMemory(mem, clickedKind = "auto") {
    const items = [];
    (Array.isArray(mem.images) ? mem.images : []).forEach((u) => items.push({ type: "image", url: u }));
    if (mem.videoUrl) items.push({ type: "video", url: mem.videoUrl });
    if (mem.audioUrl) items.push({ type: "audio", url: mem.audioUrl });
    if (!items.length) return;
    let index = 0;
    if (clickedKind === "video") index = Math.max(0, items.findIndex((i) => i.type === "video"));
    if (clickedKind === "audio") index = Math.max(0, items.findIndex((i) => i.type === "audio"));
    setLightbox({ items, index });
  }
  function lightboxNext(dir) {
    setLightbox((lb) => {
      if (!lb) return lb;
      const len = lb.items.length;
      const next = (lb.index + (dir > 0 ? 1 : -1) + len) % len;
      return { ...lb, index: next };
    });
  }

  // ✅ View actions (manager-safe)
  function viewPrint() {
    if (!viewMem) return;
    printEntry(viewMem, {
      title: viewMem?.title ? `Memory — ${viewMem.title}` : "Memory",
      galleryKey: "images",
    });
  }
  function viewDownload() {
    if (!viewMem) return;
    const filename = `memory-${(viewMem?.title || "entry").replace(/\s+/g, "-").toLowerCase()}.html`;
    downloadEntry(viewMem, {
      filename,
      title: viewMem?.title ? `Memory — ${viewMem.title}` : "Memory",
      galleryKey: "images",
    });
  }
  function viewExportTxt() {
    if (!viewMem) return;
    const txt = buildSummaryText(viewMem);
    saveTextFile(`memory-${(viewMem?.title || "entry").replace(/\s+/g, "-").toLowerCase()}.txt`, txt);
  }
  function viewForward() {
    if (!viewMem) return;
    const subject = viewMem?.title ? `Memory — ${viewMem.title}` : "Memory details";
    const body = buildSummaryText(viewMem);
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  return (
    <div className="container">
      {/* Header */}
      <div className="header-row">
        <h2>Memory Lane</h2>
        <div className="header-actions desktop-only"><ActionsDesktop /></div>
        {/* Owner-only add */}
        {!readOnly && <button className="btn-add" onClick={() => openForm()}>+</button>}
      </div>

      {/* Hero */}
      <div className="hero-wrap">
        <img src="/lastwish-account-memory.jpg" alt="Memory Lane" className="hero-img" />
      </div>
      <div className="mobile-only" style={{ margin: "10px 0" }}>
        <ActionsMobile />
      </div>

      {/* Sticky Year Filter */}
      <div ref={filterBarRef} className="ml-filter">
        <label className="ml-filter-label" htmlFor="year">Year</label>
        <select id="year" className="ml-select" value={year} onChange={(e) => setYear(e.target.value)}>
          {years.map((y) => (
            <option key={y} value={y}>{y === "all" ? "All years" : y}</option>
          ))}
        </select>
      </div>

      {/* Timeline */}
      {filtered.length === 0 ? (
        <div className="empty-state">No memories{year !== "all" ? ` in ${year}` : ""}. Use the + to add one.</div>
      ) : (
        <ol className="ml-list">
          {filtered.map((mem) => (
            <li key={mem.id} className="ml-item">
              <div className="ml-pin" aria-hidden>
                <span className="ml-dot" />
              </div>
              <article className="ml-card">
                <div className="ml-card-head">
                  <h3 className="ml-card-title">{mem.title || "Untitled"}</h3>
                  <time className="ml-card-date">
                    {mem._date
                      ? mem._date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                      : (mem.date || "Undated")}
                  </time>
                </div>

                {/* COMPACT ROW: single preview + Actions */}
                <div className="ml-compact">
                  <div className="ml-compact-left">
                    {Array.isArray(mem.images) && mem.images[0] ? (
                      <img
                        src={mem.images[0]}
                        alt=""
                        className="ml-thumb-compact"
                        loading="lazy"
                        onClick={() => openLightboxForMemory(mem, "image")}
                        title="Open gallery"
                        role="button"
                        data-allow-readonly
                      />
                    ) : mem.videoUrl ? (
                      <video
                        src={mem.videoUrl}
                        className="ml-thumb-compact"
                        muted
                        playsInline
                        onClick={() => openLightboxForMemory(mem, "video")}
                        title="Open video"
                        data-allow-readonly
                      />
                    ) : mem.audioUrl ? (
                      <div
                        className="ml-thumb-compact icon"
                        onClick={() => openLightboxForMemory(mem, "audio")}
                        title="Open audio"
                        role="button"
                        data-allow-readonly
                      >🎧</div>
                    ) : (
                      <div className="ml-thumb-compact placeholder" />
                    )}
                  </div>
                  <div className="ml-compact-right" style={{ display: "flex", gap: 8 }}>
                    {/* ✅ View is clickable for managers and styled like others */}
                    <button
                      className="btn"
                      onClick={() => setViewMem(mem)}
                      data-allow-readonly
                    >
                      View
                    </button>
                    {/* Owner-only Edit */}
                    {!readOnly && (
                      <button
                        className="btn btn-main"
                        onClick={() => openForm(mem)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              </article>
            </li>
          ))}
        </ol>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={showForm}
        title={editingId ? "Edit Memory" : "Add Memory"}
        onClose={closeForm}
        maxWidth={720}
        footer={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn btn-main" type="submit" form="memory-form" disabled={uploading}>
              {uploading ? "Saving…" : "Save"}
            </button>
            {editingId ? (
              <button className="btn btn-danger" type="button" onClick={handleDelete} disabled={uploading}>Delete</button>
            ) : null}
            <button className="btn btn-cancel" type="button" onClick={closeForm} disabled={uploading}>Cancel</button>
          </div>
        }
      >
        <form id="memory-form" onSubmit={handleSave}>
          <label className="ml-label">Title</label>
          <input className="input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g., Graduation Day" required />

          <label className="ml-label">Date</label>
          <input
            type="date"
            className="input"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            placeholder="Select a date"
          />

          <label className="ml-label">Description</label>
          <textarea className="input" rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Tell the story…" />

          <label className="ml-label">Tags</label>
          <input className="input" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="e.g., Family, School" />

          <label className="ml-label">Media (images / video / audio)</label>
          <input
            ref={fileInputMedia}
            type="file"
            accept="image/*,video/*,audio/*"
            multiple
            onChange={handleMediaFiles}
            className="input"
          />

          {(form.images.length > 0 || form.videoUrl || form.audioUrl || (form.mediaFiles || []).length > 0) && (
            <div className="ml-previews-grid">
              {form.images.map((u, i) => (
                <img key={`saved-img-${i}`} src={u} alt="" className="ml-prev" />
              ))}
              {form.videoUrl && (
                <video key="saved-video" src={form.videoUrl} controls className="ml-prev-video" />
              )}
              {form.audioUrl && (
                <audio key="saved-audio" src={form.audioUrl} controls className="ml-prev-audio" />
              )}
              {(form.mediaFiles || []).map((m, i) => (
                <div key={`new-${i}`} className="ml-prev-wrap">
                  <button type="button" className="ml-prev-remove" title="Remove" onClick={() => removeNewMedia(i)}>✖</button>
                  {/^image\//i.test(m.type) ? (
                    <img src={m.previewUrl} alt="" className="ml-prev" />
                  ) : /^video\//i.test(m.type) ? (
                    <video src={m.previewUrl} controls className="ml-prev-video" />
                  ) : /^audio\//i.test(m.type) ? (
                    <audio src={m.previewUrl} controls className="ml-prev-audio" />
                  ) : (
                    <div className="ml-prev-generic">📎 {m.file?.name || "file"}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </form>
      </Modal>

      {/* View Summary Modal */}
      <Modal
        open={!!viewMem}
        title="View Memory"
        onClose={() => setViewMem(null)}
        maxWidth={720}
        footer={
          <div className="form-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-allow-readonly>
            <button className="btn btn-main" type="button" onClick={viewPrint} data-allow-readonly>Print</button>
            <button className="btn" type="button" onClick={viewDownload} data-allow-readonly>Download</button>
            <button className="btn" type="button" onClick={viewExportTxt} data-allow-readonly>Export</button>
            <button className="btn" type="button" onClick={viewForward} data-allow-readonly>Forward</button>
            <button className="btn btn-cancel" type="button" onClick={() => setViewMem(null)} data-allow-readonly>Close</button>
          </div>
        }
      >
        {viewMem && (
          <div className="ml-view">
            <div className="ml-view-row"><b>Title:</b> {viewMem.title || "Untitled"}</div>
            <div className="ml-view-row">
              <b>Date:</b>{" "}
              {(() => {
                const d = toDate(viewMem.date) || toDate(viewMem.created);
                return d ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : (viewMem.date || "—");
              })()}
            </div>
            <div className="ml-view-row"><b>Description:</b> {viewMem.description || "—"}</div>
            <div className="ml-view-row">
              <b>Tags:</b>{" "}
              {Array.isArray(viewMem.tags) ? (viewMem.tags.join(", ") || "—") : (viewMem.tags || "—")}
            </div>

            {(Array.isArray(viewMem.images) && viewMem.images.length > 0) && (
              <>
                <div className="ml-view-sub">Images</div>
                <div className="ml-view-grid">
                  {viewMem.images.map((u, i) => (
                    <a key={`vi-${i}`} href={u} target="_blank" rel="noreferrer" className="ml-view-thumb-link" title="Open image" data-allow-readonly>
                      <img src={u} alt="" className="ml-view-thumb" loading="lazy" />
                    </a>
                  ))}
                </div>
              </>
            )}

            {viewMem.videoUrl && (
              <>
                <div className="ml-view-sub">Video</div>
                <video src={viewMem.videoUrl} controls className="ml-view-video" data-allow-readonly />
              </>
            )}

            {viewMem.audioUrl && (
              <>
                <div className="ml-view-sub">Audio</div>
                <audio src={viewMem.audioUrl} controls className="ml-view-audio" data-allow-readonly />
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Lightbox */}
      {lightbox && (
        <div className="lb-bg" onClick={() => setLightbox(null)}>
          <div className="lb-card" onClick={(e) => e.stopPropagation()}>
            <button className="lb-close" onClick={() => setLightbox(null)} aria-label="Close">×</button>
            {lightbox.items.length > 1 && (
              <>
                <button className="lb-nav lb-prev" onClick={() => lightboxNext(-1)} aria-label="Previous">‹</button>
                <button className="lb-nav lb-next" onClick={() => lightboxNext(1)} aria-label="Next">›</button>
              </>
            )}
            <div className="lb-body">
              {(() => {
                const it = lightbox.items[lightbox.index];
                if (!it) return null;
                if (it.type === "image") return <img src={it.url} alt="" className="lb-media" />;
                if (it.type === "video") return <video src={it.url} controls className="lb-media" />;
                if (it.type === "audio") return <audio src={it.url} controls className="lb-audio" />;
                return <a href={it.url} target="_blank" rel="noreferrer">Open file</a>;
              })()}
            </div>
            {lightbox.items.length > 1 && (
              <div className="lb-dots">
                {lightbox.items.map((_, i) => (
                  <button key={i} className={`lb-dot ${i === lightbox.index ? 'active' : ''}`} onClick={() => setLightbox({ ...lightbox, index: i })} aria-label={`Go to item ${i+1}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ✅ New video player modal */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://www.youtube.com/watch?v=81ZLVaITxDc"
        title="How the Memory Lane page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {/* DP drawer */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How the Memory Lane page works</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly>Print</button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" data-allow-readonly>Close</button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>Capture life events as memories—photos, videos, audio, and notes—and browse them on a timeline. Filter by year to jump quickly.</p>
              <ol>
                <li>Use <b>+</b> to add a memory with media and tags.</li>
                <li>Pick a date using the date picker (or leave blank). We’ll still place it chronologically.</li>
                <li>Edit or delete any memory later—your media stays attached.</li>
              </ol>
              <p><i>Tip:</i> Multi-select media to attach several images at once. The first chosen video/audio will be used.</p>
            </div>
          </aside>
        </div>
      )}

      {/* Scoped styles unchanged (kept) */}
      <style>{`
        .header-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
        .header-actions{display:flex;align-items:center;gap:10px}
        .desktop-only{display:none} .mobile-only{display:block}
        @media(min-width:900px){.desktop-only{display:block}.mobile-only{display:none}}

        .hero-wrap{margin:6px 0 14px}
        .hero-img{width:100%;height:130px;object-fit:cover;border-radius:14px}
        @media (max-width: 700px){
          .hero-img{height:110px}
        }

        .dp-help-row{display:flex;gap:10px;flex-wrap:wrap}
        .dp-help{border-radius:8px;padding:10px 14px;display:inline-flex;align-items:center;gap:10px;font-weight:700;color:#fff;border:none}
        .dp-help.read{background:#650202;color:#fff}
        .dp-help.see{background:#003353;color:#fff}
        .dp-help-ico{font-size:1.05rem}

        .dp-drawer-bg{position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:10000;display:flex;justify-content:flex-end}
        .dp-drawer{position:fixed;top:56px;right:0;height:calc(100vh - 56px);width:min(520px,95vw);background:#fff;box-shadow:-8px 0 24px rgba(0,0,0,0.15);border-top-left-radius:12px;border-bottom-left-radius:12px;display:flex;flex-direction:column;overflow:hidden}
        .dp-drawer-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px;border-bottom:1px solid #f0f0f0;background:#fafafa}
        .dp-drawer-title{font-weight:700;color:#2a0516}
        .dp-drawer-tools{display:flex;gap:8px}
        .dp-drawer-body{padding:16px;overflow:auto}

        /* Alternating timeline spine */
        .ml-list{position:relative;list-style:none;margin:0;padding:0}
        @media(min-width:900px){
          .ml-list{display:grid;gap:14px}
          .ml-item{display:grid;grid-template-columns:1fr 24px 1fr;gap:10px;align-items:start}
          .ml-item .ml-pin{grid-column:2;width:24px;display:flex;justify-content:center}
          .ml-item .ml-pin .ml-dot{width:10px;height:10px;border-radius:50%;background:linear-gradient(90deg,#4f46e5,#7c3aed);box-shadow:0 0 0 4px #eef2ff}
          .ml-item .ml-card{grid-column:1;background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:10px 12px;box-shadow:0 2px 10px rgba(0,0,0,.03)}
          .ml-item:nth-child(even) .ml-card{grid-column:3}
          .ml-list:before{content:"";position:absolute;left:calc(50% - 1px);top:0;bottom:0;width:2px;background:#e5e7eb}
        }
        @media(max-width:899px){
          .ml-item{position:relative;padding-left:18px}
          .ml-item .ml-pin{position:absolute;left:0;top:10px}
          .ml-item .ml-pin .ml-dot{width:9px;height:9px;border-radius:50%;background:linear-gradient(90deg,#4f46e5,#7c3aed);box-shadow:0 0 0 3px #eef2ff}
          .ml-item .ml-card{background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:10px 12px;box-shadow:0 2px 10px rgba(0,0,0,.03)}
          .ml-list:before{content:"";position:absolute;left:5px;top:0;bottom:0;width:2px;background:#e5e7eb}
        }

        .ml-card-head{display:flex;align-items:baseline;justify-content:space-between;gap:10px}
        .ml-card-title{margin:0;font-size:15px;color:#111827}
        .ml-card-date{color:#6b7280;font-size:12px}

        /* COMPACT ROW */
        .ml-compact{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:8px}
        .ml-thumb-compact{width:140px;height:90px;object-fit:cover;border-radius:10px;border:1px solid #e5e7eb;background:#f7f7fb;display:block;cursor:pointer}
        .ml-thumb-compact.icon,.ml-thumb-compact.placeholder{display:flex;align-items:center;justify-content:center;color:#6b7280;font-size:26px}
        .ml-compact-right{display:flex;align-items:center}
        @media(max-width:480px){ .ml-thumb-compact{width:120px;height:80px} }

        /* Modal previews */
        .ml-previews-grid{display:grid;grid-template-columns:repeat(auto-fill, minmax(120px,1fr));gap:10px;margin-top:8px}
        .ml-prev-wrap{position:relative}
        .ml-prev-remove{position:absolute;right:6px;top:6px;background:rgba(0,0,0,.6);color:#fff;border:0;border-radius:999px;width:22px;height:22px;line-height:22px;text-align:center;cursor:pointer}
        .ml-prev{width:100%;height:90px;object-fit:cover;border-radius:10px;border:1px solid #e5e7eb}
        .ml-prev-video{width:100%;height:120px;border-radius:10px;border:1px solid #e5e7eb;object-fit:cover;background:#000}
        .ml-prev-audio{width:100%}
        .ml-prev-generic{display:flex;align-items:center;justify-content:center;height:90px;border:1px dashed #e5e7eb;border-radius:10px;background:#fafafa;font-size:12px;color:#6b7280}

        /* Summary modal styles */
        .ml-view{display:grid;gap:10px}
        .ml-view-row{line-height:1.45}
        .ml-view-sub{margin-top:8px;font-weight:800}
        .ml-view-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(110px,1fr));gap:10px}
        .ml-view-thumb{width:100%;height:78px;object-fit:cover;border-radius:8px;border:1px solid #e5e7eb;background:#f7f7fb}
        .ml-view-video{width:100%;border-radius:10px;border:1px solid #e5e7eb;background:#000}
        .ml-view-audio{width:100%}

        /* Filter */
        .ml-filter{position:sticky;top:56px;background:#fff;z-index:5;display:flex;gap:8px;align-items:center;border:1px solid #eee;border-radius:10px;padding:8px 10px;margin:6px 0 12px}
        .ml-filter-label{font-size:12px;color:#6b7280}
        .ml-select{min-height:36px;border:1px solid #d6d6e7;border-radius:10px;padding:6px 10px}

        /* Lightbox */
        .lb-bg{position:fixed;inset:0;background:rgba(0,0,0,.8);display:flex;align-items:center;justify-content:center;z-index:10000}
        .lb-card{position:relative;max-width:min(92vw,960px);width:100%;max-height:92vh;background:#111;border-radius:14px;box-shadow:0 10px 30px rgba(0,0,0,.5);display:flex;flex-direction:column}
        .lb-body{padding:10px;display:flex;align-items:center;justify-content:center}
        .lb-media{max-width:100%;max-height:80vh;border-radius:10px}
        .lb-audio{width:100%;max-width:780px}
        .lb-close{position:absolute;top:8px;right:10px;background:transparent;border:0;color:#fff;font-size:26px;cursor:pointer}
        .lb-nav{position:absolute;top:50%;transform:translateY(-50%);background:rgba(255,255,255,.2);color:#fff;border:0;width:36px;height:48px;border-radius:8px;cursor:pointer}
        .lb-prev{left:8px} .lb-next{right:8px}
        .lb-dots{display:flex;gap:6px;justify-content:center;padding:0 0 10px}
        .lb-dot{width:8px;height:8px;border-radius:999px;background:#555;border:0;cursor:pointer}
        .lb-dot.active{background:#fff}
      `}</style>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="messages" />
    </div>
  );
}
